// Secure Discogs service that uses server-side API routes
// Credentials are stored as environment variables on Vercel

interface DiscogsCollectionResponse {
  pagination: {
    pages: number;
    page: number;
    per_page: number;
    items: number;
  };
  releases: DiscogsRelease[];
}

interface DiscogsRelease {
  id: number;
  basic_information: {
    id: number;
    master_id?: number;
    title: string;
    year: number;
    artists: Array<{
      name: string;
      role?: string;
    }>;
    genres: string[];
    styles: string[];
    formats: Array<{
      name: string;
      descriptions?: string[];
    }>;
    thumb: string;
    cover_image: string;
  };
  instance_id: number;
  folder_id: number;
}

interface DiscogsReleaseDetail {
  id: number;
  title: string;
  year: number;
  artists: Array<{
    name: string;
    role?: string;
  }>;
  genres: string[];
  styles: string[];
  formats: Array<{
    name: string;
    descriptions?: string[];
    qty: string;
  }>;
  images: Array<{
    type: string;
    uri: string;
    width: number;
    height: number;
  }>;
  tracklist?: Array<{
    position: string;
    title: string;
    duration?: string;
    artists?: Array<{
      name: string;
    }>;
  }>;
  master_id?: number;
  country?: string;
  labels: Array<{
    name: string;
  }>;
}

interface DiscogsSearchResponse {
  results: Array<{
    id: number;
    title: string;
    year?: number;
    type: string;
    artist?: string;
    thumb?: string;
    cover_image?: string;
    genre?: string[];
  }>;
  pagination: {
    pages: number;
    page: number;
    per_page: number;
    items: number;
  };
}

class SecureDiscogsService {
  private baseUrl: string;

  constructor() {
    // Use your deployed Vercel URL or current domain
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? '' // Use relative URLs in production
      : 'http://localhost:3000'; // Use localhost in development
  }

  async getCollection(page: number = 1, perPage: number = 50): Promise<DiscogsCollectionResponse> {
    const response = await fetch(`${this.baseUrl}/api/discogs/collection?page=${page}&per_page=${perPage}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch collection');
    }

    return response.json();
  }

  async getRelease(releaseId: number): Promise<DiscogsReleaseDetail> {
    const response = await fetch(`${this.baseUrl}/api/discogs/release?id=${releaseId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch release');
    }

    return response.json();
  }

  async getMasterRelease(masterId: number): Promise<DiscogsReleaseDetail> {
    const response = await fetch(`${this.baseUrl}/api/discogs/master?id=${masterId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch master release');
    }

    return response.json();
  }

  async getEnhancedRelease(releaseId: number): Promise<DiscogsReleaseDetail & { originalYear?: number }> {
    const release = await this.getRelease(releaseId);

    // If this release has a master, fetch additional data from it
    if (release.master_id) {
      try {
        const master = await this.getMasterRelease(release.master_id);

        return {
          ...release,
          originalYear: (typeof master.year === 'string' ? parseInt(master.year, 10) || 0 : master.year) ||
                        (typeof release.year === 'string' ? parseInt(release.year, 10) || 0 : release.year)
        };
      } catch (error) {
        console.warn(`Could not fetch master release ${release.master_id}:`, error);
        // Return original release data if master fetch fails
        return release;
      }
    }

    return release;
  }

  async searchReleases(query: string, perPage: number = 25): Promise<DiscogsSearchResponse> {
    const response = await fetch(`${this.baseUrl}/api/discogs/search?q=${encodeURIComponent(query)}&type=release&per_page=${perPage}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to search releases');
    }

    return response.json();
  }

  async downloadCoverImage(imageUrl: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/api/discogs/image?url=${encodeURIComponent(imageUrl)}`);
    
    if (!response.ok) {
      throw new Error('Failed to download image');
    }

    return response.blob();
  }

  // Helper methods for backward compatibility with existing code
  async getAllReleases(): Promise<DiscogsRelease[]> {
    const releases: DiscogsRelease[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await this.getCollection(page, 100);
      releases.push(...response.releases);
      totalPages = response.pagination.pages;
      page++;
    } while (page <= totalPages);

    return releases;
  }

  // Test connection method
  async testConnection(): Promise<boolean> {
    try {
      await this.getCollection(1, 1);
      return true;
    } catch (error) {
      console.error('Discogs connection test failed:', error);
      return false;
    }
  }
}

export const secureDiscogsService = new SecureDiscogsService();
export type { DiscogsCollectionResponse, DiscogsRelease, DiscogsReleaseDetail, DiscogsSearchResponse };